import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { CircularProgress, Box } from "@mui/material";

import { ProtectedRoute } from "./ProtectedRoute";
import { Layout } from "../components/Layout";
import { RequireInternalMfa } from "../components/RequireInternalMfa";
import { RequireMfaEnrollment } from "../components/RequireMfaEnrollment";
import { useHasAnyRole } from "../hooks/useHasRole";
import { useAuthStore } from "../stores/auth";
import { paths } from "./paths";

// BUG-006: must wait for auth store to finish loading before checking roles.
// Without the isLoading guard, appRoles is empty during session hydration and
// every admin navigates to /dashboard instead of the requested admin page.
function RequireAdminRole({ children }: { children: React.ReactNode }) {
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAdmin = useHasAnyRole("manna", ["admin", "revenue_leader"]);
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!isAdmin) return <Navigate to={paths.dashboard} replace />;
  return <>{children}</>;
}

const LoginPage = lazy(() => import("../features/auth/LoginPage"));
const ForgotPasswordPage = lazy(() => import("../features/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../features/auth/ResetPasswordPage"));
const OutlookAuthCallbackPage = lazy(() => import("../features/auth/OutlookAuthCallbackPage"));
const SecurityPage = lazy(() => import("../features/auth/SecurityPage"));
const MeLayout = lazy(() => import("../features/me/MeLayout"));
const ProfileTab = lazy(() => import("../features/me/ProfileTab"));
const AppearanceTab = lazy(() => import("../features/me/AppearanceTab"));
const NotificationsTab = lazy(() => import("../features/me/NotificationsTab"));
const ContactsPage = lazy(() => import("../features/contacts/ContactsPage"));
const ContactDetailPage = lazy(() => import("../features/contacts/ContactDetailPage"));
const OrganizationsPage = lazy(() => import("../features/organizations/OrganizationsPage"));
const OrganizationDetailPage = lazy(
  () => import("../features/organizations/OrganizationDetailPage"),
);
const DealsPage = lazy(() => import("../features/deals/DealsPage"));
const DealDetailPage = lazy(() => import("../features/deals/DealDetailPage"));
const DealRegistrationsPage = lazy(() => import("../features/deals/DealRegistrationsPage"));
const ReferralsPage = lazy(() => import("../features/deals/ReferralsPage"));
const DashboardPage = lazy(() => import("../features/dashboard/DashboardPage"));
const ForecastPage = lazy(() => import("../features/forecast/ForecastPage"));
const PartnersPage = lazy(() => import("../features/partners/PartnersPage"));
const TriagePage = lazy(() => import("../features/triage/TriagePage"));
const ProspectsPage = lazy(() => import("../features/prospects/ProspectsPage"));
const ActivitiesPage = lazy(() => import("../features/activities/ActivitiesPage"));
const NotFoundPage = lazy(() => import("../features/errors/NotFoundPage"));
const PublicLandingRedirect = lazy(() => import("../features/marketing/PublicLandingRedirect"));

// Agentic platform — Phase A
const AgentsPage = lazy(() => import("../features/agents/AgentsPage"));
const AgentDetailLayout = lazy(() => import("../features/agents/AgentDetail/AgentDetailLayout"));
const AgentDetailPage = lazy(() => import("../features/agents/AgentDetailPage"));
const AgentMemoryPage = lazy(() => import("../features/agents/AgentMemoryPage"));
const SkillsSubPage = lazy(() => import("../features/agents/AgentDetail/SkillsSubPage"));
const RunsSubPage = lazy(() => import("../features/agents/AgentDetail/RunsSubPage"));
const SchedulesSubPage = lazy(() => import("../features/agents/AgentDetail/SchedulesSubPage"));
const TriggersSubPage = lazy(() => import("../features/agents/AgentDetail/TriggersSubPage"));
const DreamsSubPage = lazy(() => import("../features/agents/AgentDetail/DreamsSubPage"));
const KnowledgeSubPage = lazy(() => import("../features/agents/AgentDetail/KnowledgeSubPage"));
const EvalsSubPage = lazy(() => import("../features/agents/AgentDetail/EvalsSubPage"));
const WorkflowSubPage = lazy(() => import("../features/agents/AgentDetail/WorkflowSubPage"));
const ConversationAuditPage = lazy(
  () => import("../features/agents/AdminConversationAudit/ConversationAuditPage"),
);
const ConversationDetailPage = lazy(
  () => import("../features/conversations/ConversationDetailPage"),
);
const RunsPage = lazy(() => import("../features/runs/RunsPage"));
const RunDetailPage = lazy(() => import("../features/runs/RunDetailPage"));
const ApprovalsPage = lazy(() => import("../features/approvals/ApprovalsPage"));
const SkillsPage = lazy(() => import("../features/skills/SkillsPage"));
const SkillDetailPage = lazy(() => import("../features/skills/SkillDetailPage"));
const TemplateGalleryPage = lazy(
  () => import("../features/agents/TemplateGallery/TemplateGalleryPage"),
);

// Marketing section
const CampaignsPage = lazy(() => import("../features/marketing/pages/CampaignsPage"));
const LandingPagesPage = lazy(() => import("../features/marketing/pages/LandingPagesPage"));
const LandingPuckEditorPage = lazy(
  () => import("../features/marketing/pages/LandingPuckEditorPage"),
);
const LandingAnalyticsPage = lazy(() => import("../features/marketing/pages/LandingAnalyticsPage"));

// Partner portal
const PartnerLayout = lazy(() => import("../features/partner-portal/PartnerLayout"));
const PartnerDashboardPage = lazy(
  () => import("../features/partner-portal/pages/PartnerDashboardPage"),
);
const PartnerRegisterPage = lazy(
  () => import("../features/partner-portal/pages/PartnerRegisterPage"),
);
const PartnerRegistrationsPage = lazy(
  () => import("../features/partner-portal/pages/PartnerRegistrationsPage"),
);
const PartnerDealsPage = lazy(() => import("../features/partner-portal/pages/PartnerDealsPage"));
const PartnerDealDetailPage = lazy(
  () => import("../features/partner-portal/pages/PartnerDealDetailPage"),
);
const PartnerCommissionsPage = lazy(
  () => import("../features/partner-portal/pages/PartnerCommissionsPage"),
);

// Admin section
const AdminLayout = lazy(() => import("../features/admin/AdminLayout"));
const UsersPage = lazy(() => import("../features/admin/pages/UsersPage"));
const PipelinesPage = lazy(() => import("../features/admin/pages/PipelinesPage"));
const LeadSourcesPage = lazy(() => import("../features/admin/pages/LeadSourcesPage"));
const RolesPage = lazy(() => import("../features/admin/pages/RolesPage"));
const TerritoriesPage = lazy(() => import("../features/admin/pages/TerritoriesPage"));
const PartnerCompaniesPage = lazy(() => import("../features/admin/pages/PartnerCompaniesPage"));
const AuditLogPage = lazy(() => import("../features/admin/pages/AuditLogPage"));
const ReferenceTablesPage = lazy(() => import("../features/admin/pages/ReferenceTablesPage"));
const ImportPage = lazy(() => import("../features/admin/pages/ImportPage"));
const DuplicatesPage = lazy(() => import("../features/admin/pages/DuplicatesPage"));
const IntegrationsAdminPage = lazy(() => import("../features/admin/pages/IntegrationsAdminPage"));
const TagsPage = lazy(() => import("../features/admin/pages/TagsPage"));
const TeamsWebhooksPage = lazy(() => import("../features/admin/pages/TeamsWebhooksPage"));
const CommissionsPage = lazy(() => import("../features/admin/pages/CommissionsPage"));
const IntegrationsTab = lazy(() => import("../features/me/IntegrationsTab"));
const OutlookConnectCallbackPage = lazy(() => import("../features/me/OutlookConnectCallbackPage"));
const CommissionsTab = lazy(() => import("../features/me/CommissionsTab"));
const AccountListsPage = lazy(() => import("../features/marketing/pages/AccountListsPage"));
const AccountListDetailPage = lazy(
  () => import("../features/marketing/pages/AccountListDetailPage"),
);

function RouteFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <CircularProgress />
    </Box>
  );
}

function wrap(el: React.ReactElement) {
  return <Suspense fallback={<RouteFallback />}>{el}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: paths.login,
    element: wrap(<LoginPage />),
  },
  {
    path: paths.forgotPassword,
    element: wrap(<ForgotPasswordPage />),
  },
  {
    path: paths.resetPassword,
    element: wrap(<ResetPasswordPage />),
  },
  {
    // Public — opened inside the Outlook add-in's sign-in dialog (no auth guard,
    // no Layout). Completes Supabase-Azure OAuth and posts the session back.
    path: paths.outlookAuthCallback,
    element: wrap(<OutlookAuthCallbackPage />),
  },
  {
    // Public embed route — no auth, no Layout. Embeddable via iframe.
    path: paths.publicLandingPage,
    element: wrap(<PublicLandingRedirect />),
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <RequireMfaEnrollment>
          <RequireInternalMfa>
            <Layout />
          </RequireInternalMfa>
        </RequireMfaEnrollment>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to={paths.dashboard} replace /> },
      { path: paths.dashboard, element: wrap(<DashboardPage />) },
      { path: paths.contacts, element: wrap(<ContactsPage />) },
      { path: paths.contactDetail, element: wrap(<ContactDetailPage />) },
      { path: paths.organizations, element: wrap(<OrganizationsPage />) },
      { path: paths.organizationDetail, element: wrap(<OrganizationDetailPage />) },
      { path: paths.deals, element: wrap(<DealsPage />) },
      // Static /deals/registrations + /deals/referrals must come before the :id param.
      { path: paths.dealRegistrations, element: wrap(<DealRegistrationsPage />) },
      { path: paths.referrals, element: wrap(<ReferralsPage />) },
      { path: paths.dealDetail, element: wrap(<DealDetailPage />) },
      { path: paths.partners, element: wrap(<PartnersPage />) },
      { path: paths.triage, element: wrap(<TriagePage />) },
      { path: paths.prospects, element: wrap(<ProspectsPage />) },
      // Retired 2026-06-08: folded into the Deal Registrations hub. Keep the
      // path as a redirect so old links / bookmarks land on the pending tab.
      {
        path: paths.partnerSubmissions,
        element: <Navigate to={`${paths.dealRegistrations}?tab=pending`} replace />,
      },
      { path: paths.activities, element: wrap(<ActivitiesPage />) },
      { path: paths.forecast, element: wrap(<ForecastPage />) },
      { path: paths.security, element: wrap(<SecurityPage />) },
      {
        path: paths.me,
        element: wrap(<MeLayout />),
        children: [
          { index: true, element: <Navigate to={paths.meProfile} replace /> },
          { path: paths.meProfile, element: wrap(<ProfileTab />) },
          { path: paths.meAppearance, element: wrap(<AppearanceTab />) },
          { path: paths.meNotifications, element: wrap(<NotificationsTab />) },
          { path: paths.meIntegrations, element: wrap(<IntegrationsTab />) },
          { path: paths.meCommissions, element: wrap(<CommissionsTab />) },
        ],
      },
      // OAuth redirect target — standalone (no MeLayout sub-nav) so it can
      // process the code + redirect back to Integrations.
      { path: paths.outlookConnectCallback, element: wrap(<OutlookConnectCallbackPage />) },

      // Agentic platform — flat routes; nav lives in sidebar
      { path: paths.agents, element: wrap(<AgentsPage />) },
      // Static /agents/new must come before the :id param so it isn't captured.
      { path: paths.agentNew, element: <Navigate to={paths.agents} replace /> },
      {
        path: paths.agentDetail,
        element: wrap(<AgentDetailLayout />),
        children: [
          { index: true, element: <Navigate to="settings" replace /> },
          { path: "settings", element: wrap(<AgentDetailPage />) },
          { path: "skills", element: wrap(<SkillsSubPage />) },
          { path: "memory", element: wrap(<AgentMemoryPage />) },
          { path: "runs", element: wrap(<RunsSubPage />) },
          { path: "schedules", element: wrap(<SchedulesSubPage />) },
          { path: "triggers", element: wrap(<TriggersSubPage />) },
          { path: "dreams", element: wrap(<DreamsSubPage />) },
          { path: "knowledge", element: wrap(<KnowledgeSubPage />) },
          { path: "evals", element: wrap(<EvalsSubPage />) },
          { path: "workflow", element: wrap(<WorkflowSubPage />) },
        ],
      },
      { path: paths.conversations, element: wrap(<ConversationAuditPage />) },
      { path: paths.templates, element: wrap(<TemplateGalleryPage />) },
      { path: paths.conversationDetail, element: wrap(<ConversationDetailPage />) },
      { path: paths.runs, element: wrap(<RunsPage />) },
      { path: paths.runDetail, element: wrap(<RunDetailPage />) },
      { path: paths.approvals, element: wrap(<ApprovalsPage />) },
      { path: paths.skills, element: wrap(<SkillsPage />) },
      { path: paths.skillDetail, element: wrap(<SkillDetailPage />) },

      // Marketing section — flat routes under /marketing, nav lives in sidebar
      { path: paths.marketing, element: <Navigate to={paths.marketingCampaigns} replace /> },
      { path: paths.marketingCampaigns, element: wrap(<CampaignsPage />) },
      { path: paths.marketingLandingPages, element: wrap(<LandingPagesPage />) },
      { path: paths.marketingLandingPageAnalytics, element: wrap(<LandingAnalyticsPage />) },
      { path: paths.marketingLandingPageEdit, element: wrap(<LandingPuckEditorPage />) },
      { path: paths.marketingAccountLists, element: wrap(<AccountListsPage />) },
      { path: paths.marketingAccountListDetail, element: wrap(<AccountListDetailPage />) },

      // Admin section — nested layout, nav lives in the main sidebar
      {
        path: paths.admin,
        element: <RequireAdminRole>{wrap(<AdminLayout />)}</RequireAdminRole>,
        children: [
          { index: true, element: <Navigate to={paths.adminUsers} replace /> },
          { path: paths.adminUsers, element: wrap(<UsersPage />) },
          { path: paths.adminRoles, element: wrap(<RolesPage />) },
          { path: paths.adminPipelines, element: wrap(<PipelinesPage />) },
          { path: paths.adminLeadSources, element: wrap(<LeadSourcesPage />) },
          { path: paths.adminTerritories, element: wrap(<TerritoriesPage />) },
          { path: paths.adminPartnerCompanies, element: wrap(<PartnerCompaniesPage />) },
          { path: paths.adminReferenceTables, element: wrap(<ReferenceTablesPage />) },
          { path: paths.adminAuditLog, element: wrap(<AuditLogPage />) },
          { path: paths.adminImport, element: wrap(<ImportPage />) },
          { path: paths.adminDuplicatesContacts, element: wrap(<DuplicatesPage />) },
          { path: paths.adminDuplicatesOrganizations, element: wrap(<DuplicatesPage />) },
          {
            path: paths.adminDuplicates,
            element: <Navigate to={paths.adminDuplicatesContacts} replace />,
          },
          { path: paths.adminIntegrations, element: wrap(<IntegrationsAdminPage />) },
          { path: paths.adminTags, element: wrap(<TagsPage />) },
          { path: paths.adminTeamsWebhooks, element: wrap(<TeamsWebhooksPage />) },
          { path: paths.adminCommissions, element: wrap(<CommissionsPage />) },
        ],
      },
    ],
  },
  {
    // Partner portal — separate top-level route group with its own layout.
    path: paths.partnerHome,
    element: (
      <ProtectedRoute>
        <PartnerLayoutWrap />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to={paths.partnerDashboard} replace /> },
      { path: paths.partnerDashboard, element: wrap(<PartnerDashboardPage />) },
      { path: paths.partnerRegister, element: wrap(<PartnerRegisterPage />) },
      { path: paths.partnerRegistrations, element: wrap(<PartnerRegistrationsPage />) },
      { path: paths.partnerDeals, element: wrap(<PartnerDealsPage />) },
      { path: paths.partnerDealDetail, element: wrap(<PartnerDealDetailPage />) },
      { path: paths.partnerCommissions, element: wrap(<PartnerCommissionsPage />) },
    ],
  },
  {
    path: "*",
    element: wrap(<NotFoundPage />),
  },
]);

// PartnerLayout is lazy-loaded; suspense-wrap it at the layout level too.
function PartnerLayoutWrap() {
  return wrap(<PartnerLayout />);
}
